import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  TreeSelect,
  Select,
  Space,
  Button,
  message
} from 'antd';
import Lang from '../../common/model/global/Lang';
import Dept, { DeptType, DeptTypeList } from '../../common/model/department/Dept';

const { TextArea } = Input;

interface DeptModalProps {
  visible: boolean;
  type: 'add' | 'edit' | 'view';
  dept: Dept;
  allDepts: Dept[];
  onCancel: () => void;
  onSuccess: () => void;
}

const DeptModal: React.FC<DeptModalProps> = ({
  visible,
  type,
  dept,
  allDepts,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [parentOptions, setParentOptions] = useState<any[]>([]);

  // 是否只读
  const isViewMode = type === 'view';

  // 初始化数据
  useEffect(() => {
    if (visible) {
      console.log('DeptModal 初始化，type:', type, 'dept:', dept);
      
      // 构建父部门选项
      const options = buildParentDeptOptions(allDepts, type === 'edit' ? dept?.id : null);
      setParentOptions(options);
      
      // 设置表单值
      if (dept) {
        const formValues: any = {
          name: dept.name || '',
          shortName: dept.shortName || '',
          type: dept.type || DeptType.DEPARTMENT,
          memo: dept.memo || '',
        };
        
        // 处理父部门ID：0表示根部门，显示为空
        if (dept.parentId && dept.parentId !== 0) {
          formValues.parentId = dept.parentId.toString();
        } else {
          formValues.parentId = '';
        }
        
        console.log('设置表单值:', formValues);
        form.setFieldsValue(formValues);
      } else {
        form.resetFields();
        // 新建时设置默认值
        form.setFieldsValue({
          type: DeptType.DEPARTMENT,
          parentId: '' // 默认为空（根部门）
        });
      }
    }
  }, [visible, dept, allDepts, type, form]);

  // 构建父部门下拉选项
  const buildParentDeptOptions = (data: Dept[], excludeId?: number | null): any[] => {
    if (!data || data.length === 0) return [];
    
    // 过滤部门，排除当前编辑的部门及其子部门
    const filteredDepts = data.filter(item => {
      // 排除当前编辑的部门
      if (excludeId && item.id === excludeId) {
        return false;
      }
      return true;
    });
    
    // 为根部门添加一个选项
    const options: any[] = [
      {
        value: '',
        label: Lang.t('department.noParentDept'),
        level: 0
      }
    ];
    
    // 添加部门选项，按层级缩进显示
    filteredDepts.forEach(item => {
      // 生成缩进前缀
      const indent = item.level && item.level > 1 ? '  '.repeat(item.level - 1) + '├─ ' : '';
      options.push({
        value: item.id ? item.id.toString() : '',
        label: `${indent}${item.shortName} (${item.name})`,
        level: item.level || 0
      });
    });
    
    return options;
  };

  // 处理提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log('表单提交值:', values);
      
      setLoading(true);
      
      // 创建部门实例
      const deptInstance = new Dept();
      
      // 如果是编辑模式，保留原有的id
      if (type === 'edit' && dept.id) {
        deptInstance.id = dept.id;
      }
      
      // 设置表单值
      deptInstance.name = values.name;
      deptInstance.shortName = values.shortName;
      deptInstance.type = values.type;
      deptInstance.memo = values.memo || '';
      
      // 处理父部门ID：空字符串转为0（根部门）
      deptInstance.parentId = values.parentId ? parseInt(values.parentId, 10) : 0;
      
      console.log('保存前的部门实例:', deptInstance);
      
      // 保存部门
      deptInstance.httpSave(
        (response: any) => {
          console.log('保存部门响应:', response);
          
          // 统一处理响应
          let success = false;
          let msg = '';
          
          // 检查响应结构
          if (response) {
            // 方法1：直接检查数据字段
            if (response.data) {
              const responseData = response.data;
              
              // 检查 code 字段
              if (responseData.code === "OK" || responseData.code === "SUCCESS" || 
                  responseData.Code === "OK" || responseData.Code === "SUCCESS" ||
                  response.code === "OK" || response.code === "SUCCESS") {
                success = true;
                msg = responseData.msg || responseData.Msg || responseData.message || Lang.t('operationSuccess');
              }
              // 检查是否有部门数据
              else if (responseData.id || responseData.name) {
                success = true;
                msg = Lang.t('operationSuccess');
              }
            }
            // 方法2：检查是否是直接的部门数据
            else if (response.id || response.name) {
              success = true;
              msg = Lang.t('operationSuccess');
            }
            // 方法3：检查标准 WebResult 结构
            else if (response.Code === "OK" || response.code === "OK") {
              success = true;
              msg = response.Msg || response.msg || response.message || Lang.t('operationSuccess');
            }
          }
          
          if (success) {
            message.success(msg);
            onSuccess();
          } else {
            const errorMsg = response?.msg || response?.Msg || response?.message || Lang.t('operationFailed');
            message.error(errorMsg);
          }
        },
        (error: any) => {
          console.error('保存失败:', error);
          // 显示具体的错误信息
          if (error && error.message) {
            message.error(error.message);
          } else if (error && error.msg) {
            message.error(error.msg);
          } else if (error && error.Msg) {
            message.error(error.Msg);
          } else {
            message.error(Lang.t('operationFailed'));
          }
        },
        () => {
          setLoading(false);
        }
      );
    } catch (error: any) {
      console.error('表单验证失败:', error);
      if (error.errorFields) {
        // 表单验证错误，由antd自动显示
        return;
      }
      message.error(error.message || Lang.t('operationFailed'));
    }
  };

  // 弹窗标题
  const getModalTitle = () => {
    switch (type) {
      case 'add':
        return Lang.t('department.createDept');
      case 'edit':
        return Lang.t('department.editDept');
      case 'view':
        return Lang.t('department.viewDept');
      default:
        return '';
    }
  };

  // 渲染表单字段（根据查看模式禁用）
  const renderFormItem = (children: React.ReactElement) => {
    if (isViewMode) {
      return React.cloneElement(children, {
        disabled: true,
        readOnly: true
      });
    }
    return children;
  };

  // 获取父部门名称
  const getParentDeptName = () => {
    if (!dept.parentId || dept.parentId === 0) {
      return Lang.t('department.noParentDept');
    }
    
    const parentDept = allDepts.find(item => item.id === dept.parentId);
    if (parentDept) {
      return `${parentDept.shortName} (${parentDept.name})`;
    }
    
    return `ID: ${dept.parentId}`;
  };

  return (
    <Modal
      title={getModalTitle()}
      visible={visible}
      width={650}
      style={{ top: '5%' }} // 设置距离顶部5%的位置
      bodyStyle={{ 
        maxHeight: '70vh', // 限制最大高度为视口的70%
        overflowY: 'auto',  // 添加垂直滚动条
        padding: '16px 24px' // 调整内边距
      }}
      centered={true} // 垂直居中显示
      onCancel={onCancel}
      footer={
        !isViewMode ? (
          <Space>
            <Button onClick={onCancel}>
              {Lang.t('cancel')}
            </Button>
            <Button
              type="primary"
              loading={loading}
              onClick={handleSubmit}
            >
              {Lang.t('confirm')}
            </Button>
          </Space>
        ) : (
          <Button onClick={onCancel}>
            {Lang.t('close')}
          </Button>
        )
      }
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        size="middle" // 使用中等尺寸，更紧凑
      >
        <Form.Item
          name="name"
          label={Lang.t('department.fullName')}
          rules={[
            { required: true, message: Lang.t('department.fullNameRequired') },
            { max: 100, message: Lang.t('department.fullNameMaxLength') }
          ]}
        >
          {renderFormItem(
            <Input 
              placeholder={Lang.t('department.fullNamePlaceholder')}
            />
          )}
        </Form.Item>

        <Form.Item
          name="shortName"
          label={Lang.t('department.shortName')}
          rules={[
            { required: true, message: Lang.t('department.shortNameRequired') },
            { max: 50, message: Lang.t('department.shortNameMaxLength') }
          ]}
        >
          {renderFormItem(
            <Input 
              placeholder={Lang.t('department.shortNamePlaceholder')}
            />
          )}
        </Form.Item>

        <Form.Item
          name="parentId"
          label={Lang.t('department.parentDept')}
        >
          {renderFormItem(
            <Select
              placeholder={Lang.t('department.selectParentDept')}
              style={{ width: '100%' }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label as string || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {parentOptions.map(option => (
                <Select.Option 
                  key={option.value} 
                  value={option.value}
                  disabled={isViewMode}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item
          name="type"
          label={Lang.t('department.type')}
          rules={[{ required: true, message: Lang.t('department.typeRequired') }]}
        >
          {renderFormItem(
            <Select
              placeholder={Lang.t('department.selectType')}
              style={{ width: '100%' }}
            >
              {DeptTypeList.map(item => (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item
          name="memo"
          label={Lang.t('department.remark')}
        >
          {renderFormItem(
            <TextArea 
              rows={2} // 从4行减少到2行
              placeholder={Lang.t('department.remarkPlaceholder')}
              maxLength={500}
              showCount
              style={{ minHeight: '60px' }} // 设置最小高度
            />
          )}
        </Form.Item>

        {/* 查看模式下显示更多信息 */}
        {isViewMode && dept && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', // 两列布局
            gap: '16px' // 列间距
          }}>
            <Form.Item
              label={Lang.t('department.id')}
              style={{ marginBottom: '12px' }} // 减少底部边距
            >
              <Input 
                value={dept.id ? dept.id.toString() : ''}
                readOnly
                size="small" // 使用小尺寸
              />
            </Form.Item>
            
            <Form.Item
              label={Lang.t('department.level')}
              style={{ marginBottom: '12px' }}
            >
              <Input 
                value={dept.level ? `第 ${dept.level} 级` : '--'}
                readOnly
                size="small"
              />
            </Form.Item>
            
            <Form.Item
              label={Lang.t('department.status')}
              style={{ marginBottom: '12px' }}
            >
              <Input 
                value={dept.getStatusText?.() || (dept.status === '1' ? Lang.t('department.enabled') : Lang.t('department.disabled'))}
                readOnly
                size="small"
              />
            </Form.Item>
            
            <Form.Item
              label={Lang.t('department.orderIndex')}
              style={{ marginBottom: '12px' }}
            >
              <Input 
                value={dept.orderIndex ? dept.orderIndex.toString() : '0'}
                readOnly
                size="small"
              />
            </Form.Item>
            
            <Form.Item
              label={Lang.t('department.parentDept')}
              style={{ gridColumn: 'span 2', marginBottom: '12px' }} // 跨两列
            >
              <Input 
                value={getParentDeptName()}
                readOnly
                size="small"
              />
            </Form.Item>
            
            {dept.createUser && (
              <Form.Item
                label={Lang.t('department.createUser')}
                style={{ marginBottom: '12px' }}
              >
                <Input 
                  value={dept.createUser}
                  readOnly
                  size="small"
                />
              </Form.Item>
            )}
            
            {dept.updateUser && (
              <Form.Item
                label={Lang.t('department.updateUser')}
                style={{ marginBottom: '12px' }}
              >
                <Input 
                  value={dept.updateUser}
                  readOnly
                  size="small"
                />
              </Form.Item>
            )}
            
            {dept.createTime && (
              <Form.Item
                label={Lang.t('createTime')}
                style={{ marginBottom: '12px' }}
              >
                <Input 
                  value={dept.getCreateTimeFormatted?.() || String(dept.createTime)}
                  readOnly
                  size="small"
                />
              </Form.Item>
            )}
            
            {dept.updateTime && (
              <Form.Item
                label={Lang.t('updateTime')}
                style={{ marginBottom: '12px' }}
              >
                <Input 
                  value={dept.getUpdateTimeFormatted?.() || String(dept.updateTime)}
                  readOnly
                  size="small"
                />
              </Form.Item>
            )}
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default DeptModal;